import React, { memo } from "react";
import "../styles.css";
import { COLORS } from "../constants";
import { DocumentsObjectType, SalaryDetailsData } from "../types";

interface SalaryDetailsSectionProps {
  salaryData?: SalaryDetailsData;
  handleEditSalary: () => void;
  handleDocumentView: (document: DocumentsObjectType) => void;
  reCalculateSimahData: () => void;
  enableButtons: boolean;
}



const SalaryDetailsSection: React.FC<SalaryDetailsSectionProps> = ({
  salaryData,
  handleEditSalary,
  handleDocumentView,
  reCalculateSimahData,
  enableButtons,
}) => {
  // Use provided data or fall back to mock data for development
  let documentsObject: DocumentsObjectType[] = [];
  if (salaryData?.bankStatementUrl) {
    documentsObject.push({ docUrl: salaryData.bankStatementUrl, title: "Bank_Statement.pdf", docType: "bankDoc" });
  }
  if (salaryData?.gosiCertificateUrl) {
    documentsObject.push({ docUrl: salaryData.gosiCertificateUrl, title: "Gosi_Certificate.pdf", docType: "gosiDoc"  });
  }
  if (salaryData?.salaryCertificateUrl) {
    documentsObject.push({ docUrl: salaryData.salaryCertificateUrl, title: "Salary_Statement.pdf", docType: "salaryDoc"  });
  }



  const getEmploymentName = (code: string): string => {
    switch (code) {
      case "P":
        return "Private";
      case "R":
        return "Retired";
      case "GM":
        return "Government-Military";
      case "GNM":
        return "Government-Non-Military";
      case "UE":
        return "Unemployed";
      default:
        return "";
    }
  };

  const formatPayMonth = (value?: string): string => {
    if (!value) return "-";
    
    try {
      // Check if value is in YYYYMM format (6 digits)
      if (typeof value === "string" && /^\d{6}$/.test(value)) {
        const year = parseInt(value.substring(0, 4), 10);
        const month = parseInt(value.substring(4, 6), 10);
        
        if (year > 0 && month >= 1 && month <= 12) {
          const monthNames = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
          ];
          return `${monthNames[month - 1]} ${year}`;
        }
      }
      
      // Return original value if format doesn't match
      return value;
    } catch {
      return value || "-";
    }
  };

  return (
    <div>
      {/* Header row */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <p
          className="text-dark fw-bold fs-5 m-0"
          style={{ color: COLORS.TEXT_PRIMARY }}
        >
          Salary Details
        </p>
        {enableButtons && (
          <div className="d-flex gap-3">
            <button
              type="button"
              className="btn border d-flex align-items-center gap-2 px-4"
              onClick={handleEditSalary}
            >
              <i className="bi bi-pencil-square"></i>
              <span>Edit Salary Details</span>
            </button>

            <button
              type="button"
              className="btn border d-flex align-items-center gap-2 px-4"
              onClick={reCalculateSimahData}
            >
              <span className="text-success">
                <i className="bi bi-plus-lg"></i>
              </span>
              <span>Recalculate SIMAH data</span>
            </button>
          </div>
        )}
      </div>

      {/* Divider */}
      <hr />

      {/* Salary table */}
      <div className="rounded-4 p-1">
        <table className="table text-center table-border spaced-table">
          <thead style={{ background: COLORS.BG_LIGHTER }}>
            <tr style={{ color: COLORS.TEXT_MUTED }}>
              <th className="fw-mediumNormal">Basic</th>
              <th className="fw-mediumNormal">Housing</th>
              <th className="fw-mediumNormal">Other allowance</th>
              <th className="fw-mediumNormal">Employer Name</th>
              <th className="fw-mediumNormal">Date of joining</th>
              <th className="fw-mediumNormal">Pay month</th>
              <th className="fw-mediumNormal">Status</th>
              {/* <th className="fw-mediumNormal">Salary certificate</th> */}
              {/* <th className="fw-mediumNormal">Bank Statement</th> */}
              <th className="fw-mediumNormal">Employment sector</th>
            </tr>
          </thead>
          <tbody style={{ color: COLORS.TEXT_MUTED }}>
            {salaryData && (
              <tr>
                <td>{salaryData?.basicSalary ?? "-"}</td>
                <td>{salaryData?.housingAllowance ?? "-"}</td>
                <td>{salaryData?.otherAllowance ?? "-"}</td>
                <td>{salaryData?.employerName ?? "-"}</td>
                <td>{salaryData?.dateOfJoining ?? "-"}</td>
                <td>{ salaryData?.payMonth ? formatPayMonth(salaryData.payMonth) : "-"}</td>
                <td>{salaryData?.salaryStatus ?? "-"}</td>
                {/* <td>{salaryData.salaryCertificate}</td>
                <td>{salaryData.bankStatement}</td> */}
                <td>{getEmploymentName(salaryData?.sector ?? "")}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Additional documents */}
      { documentsObject &&
        documentsObject.length > 0 && (
          <div>
            <h5 className="fw-semibold mb-3">Additional documents</h5>
            <div className="d-flex flex-wrap gap-3">
              {documentsObject.map((doc, index) => (
                <button
                  key={index}
                  type="button"
                  className="btn border d-flex align-items-center gap-2 px-4 py-2"
                  onClick={()=> handleDocumentView(doc)}
                >
                  <i className="bi bi-file-earmark-text"></i>
                  <span>{doc.title}</span>
                </button>
              ))}
            </div>
          </div>
        )}
    </div>
  );
};

export default memo(SalaryDetailsSection);
